import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import {
  Plus, Search, X, Server, Link as LinkIcon, User, KeyRound, Phone, Pencil, Trash2,
  Eye, EyeOff, Copy, Building2, DollarSign, FileText,
} from "lucide-react";

const fmtCurrency = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function CreditosPage() {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [visibleSenhas, setVisibleSenhas] = useState({});
  const [form, setForm] = useState({
    servidor: "", valor: "", link: "", links_extras: [], login: "", senha: "",
    fornecedor: "", contato_1: "", contato_2: "", descricao: "",
  });

  const fetchItems = useCallback(async () => {
    try {
      const res = await authAxios.get("/creditos");
      setItems(res.data);
    } catch {
      toast.error("Erro ao carregar creditos");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => {
    setEditing(null);
    setForm({
      servidor: "", valor: "", link: "", links_extras: [], login: "", senha: "",
      fornecedor: "", contato_1: "", contato_2: "", descricao: "",
    });
  };

  const openCreate = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      servidor: item.servidor || "",
      valor: String(item.valor ?? ""),
      link: item.link || "",
      links_extras: Array.isArray(item.links_extras) ? [...item.links_extras] : [],
      login: item.login || "",
      senha: item.senha || "",
      fornecedor: item.fornecedor || "",
      contato_1: item.contato_1 || "",
      contato_2: item.contato_2 || "",
      descricao: item.descricao || "",
    });
    setDialogOpen(true);
  };

  const addLinkExtra = () => {
    setForm(f => ({ ...f, links_extras: [...f.links_extras, ""] }));
  };

  const updateLinkExtra = (idx, val) => {
    setForm(f => {
      const arr = [...f.links_extras];
      arr[idx] = val;
      return { ...f, links_extras: arr };
    });
  };

  const removeLinkExtra = (idx) => {
    setForm(f => ({ ...f, links_extras: f.links_extras.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.servidor.trim()) { toast.error("Informe o servidor"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        valor: parseFloat(form.valor || 0),
        links_extras: form.links_extras.filter(l => l && l.trim()),
      };
      if (editing) {
        await authAxios.put(`/creditos/${editing.id}`, payload);
        toast.success("Credito atualizado!");
      } else {
        await authAxios.post("/creditos", payload);
        toast.success("Credito adicionado!");
      }
      setDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Excluir credito "${item.servidor}"?`)) return;
    try {
      await authAxios.delete(`/creditos/${item.id}`);
      toast.success("Excluido!");
      fetchItems();
    } catch { toast.error("Erro ao excluir"); }
  };

  const toggleSenha = (id) => {
    setVisibleSenhas(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  const filtered = items.filter(it =>
    it.servidor.toLowerCase().includes(search.toLowerCase()) ||
    (it.fornecedor || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Creditos</h1>
          <p className="text-muted-foreground text-sm">
            Cadastro de servidores, logins e fornecedores de creditos
          </p>
        </div>
        <Button onClick={openCreate} data-testid="creditos-novo-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Credito
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-primary/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><Server className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{items.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10"><Building2 className="w-5 h-5 text-blue-500" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Fornecedores</p>
              <p className="text-2xl font-bold text-blue-500">
                {new Set(items.map(i => i.fornecedor).filter(Boolean)).size}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10"><DollarSign className="w-5 h-5 text-emerald-500" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Valor Total</p>
              <p className="text-2xl font-bold text-emerald-500">
                {fmtCurrency(items.reduce((acc, i) => acc + (i.valor || 0), 0))}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          className="pl-9 pr-9"
          placeholder="Buscar por servidor ou fornecedor..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="creditos-search-input"
        />
        {search && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-red-500 hover:text-red-600"
            onClick={() => setSearch("")}
            data-testid="creditos-search-clear"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <p className="text-center py-12 text-muted-foreground">Carregando...</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Server className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Nenhum credito cadastrado</p>
            <Button onClick={openCreate} className="mt-4" variant="outline">
              <Plus className="w-4 h-4 mr-2" /> Cadastrar primeiro credito
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(item => (
            <Card key={item.id} className="border-primary/30" data-testid={`credito-card-${item.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      <Server className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-lg truncate">{item.servidor}</CardTitle>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {item.valor > 0 && (
                          <Badge className="text-xs bg-emerald-500/15 text-emerald-500 border-emerald-500/30">
                            {fmtCurrency(item.valor)}
                          </Badge>
                        )}
                        {item.fornecedor && (
                          <Badge variant="outline" className="text-xs">
                            <Building2 className="w-3 h-3 mr-1" /> {item.fornecedor}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2 space-y-2">
                {item.link && (
                  <div className="flex items-center gap-2 text-xs p-2 rounded-md bg-muted/30">
                    <LinkIcon className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="truncate flex-1 text-blue-400 hover:underline">{item.link}</a>
                    <button onClick={() => copyText(item.link, "Link")} className="text-muted-foreground hover:text-primary"><Copy className="w-3 h-3" /></button>
                  </div>
                )}
                {Array.isArray(item.links_extras) && item.links_extras.map((lnk, idx) => (
                  <div key={`extra-${idx}`} className="flex items-center gap-2 text-xs p-2 rounded-md bg-muted/30" data-testid={`card-link-extra-${item.id}-${idx}`}>
                    <LinkIcon className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                    <a href={lnk} target="_blank" rel="noopener noreferrer" className="truncate flex-1 text-blue-400 hover:underline">{lnk}</a>
                    <button onClick={() => copyText(lnk, "Link")} className="text-muted-foreground hover:text-primary"><Copy className="w-3 h-3" /></button>
                  </div>
                ))}
                {item.login && (
                  <div className="flex items-center gap-2 text-xs p-2 rounded-md bg-muted/30">
                    <User className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span className="truncate flex-1 font-mono">{item.login}</span>
                    <button onClick={() => copyText(item.login, "Login")} className="text-muted-foreground hover:text-primary"><Copy className="w-3 h-3" /></button>
                  </div>
                )}
                {item.senha && (
                  <div className="flex items-center gap-2 text-xs p-2 rounded-md bg-muted/30">
                    <KeyRound className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span className="truncate flex-1 font-mono">
                      {visibleSenhas[item.id] ? item.senha : "••••••••"}
                    </span>
                    <button onClick={() => toggleSenha(item.id)} className="text-muted-foreground hover:text-primary" data-testid={`toggle-senha-${item.id}`}>
                      {visibleSenhas[item.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </button>
                    <button onClick={() => copyText(item.senha, "Senha")} className="text-muted-foreground hover:text-primary"><Copy className="w-3 h-3" /></button>
                  </div>
                )}
                {(item.contato_1 || item.contato_2) && (
                  <div className="space-y-1">
                    {item.contato_1 && (
                      <div className="flex items-center gap-2 text-xs p-2 rounded-md bg-muted/30">
                        <Phone className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span className="truncate flex-1">{item.contato_1}</span>
                        <a
                          href={`https://wa.me/${item.contato_1.replace(/\D/g, "")}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-emerald-500 hover:text-emerald-400 text-[10px] font-semibold"
                        >WhatsApp</a>
                      </div>
                    )}
                    {item.contato_2 && (
                      <div className="flex items-center gap-2 text-xs p-2 rounded-md bg-muted/30">
                        <Phone className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span className="truncate flex-1">{item.contato_2}</span>
                        <a
                          href={`https://wa.me/${item.contato_2.replace(/\D/g, "")}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-emerald-500 hover:text-emerald-400 text-[10px] font-semibold"
                        >WhatsApp</a>
                      </div>
                    )}
                  </div>
                )}

                {item.descricao && (
                  <div className="flex items-start gap-2 text-xs p-2 rounded-md bg-muted/30">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                    <p className="flex-1 whitespace-pre-wrap break-words text-muted-foreground" data-testid={`card-descricao-${item.id}`}>{item.descricao}</p>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline" size="sm" className="flex-1"
                    onClick={() => openEdit(item)}
                    data-testid={`credito-edit-${item.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                  </Button>
                  <Button
                    variant="outline" size="icon" className="h-9 w-9 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(item)}
                    data-testid={`credito-delete-${item.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Create/Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Credito" : "Novo Credito"}</DialogTitle>
            <DialogDescription>
              Cadastre as informacoes de acesso e contato do fornecedor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="credito-servidor">Servidor *</Label>
                <Input
                  id="credito-servidor"
                  placeholder="Ex: UNITV, ARENA, SPARK..."
                  value={form.servidor}
                  onChange={e => setForm({ ...form, servidor: e.target.value })}
                  data-testid="credito-servidor-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="credito-valor">Valor (R$)</Label>
                <Input
                  id="credito-valor"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={form.valor}
                  onChange={e => setForm({ ...form, valor: e.target.value })}
                  data-testid="credito-valor-input"
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="credito-link">Link</Label>
                <Button
                  type="button" variant="ghost" size="sm"
                  className="h-7 px-2 text-primary hover:text-primary"
                  onClick={addLinkExtra}
                  data-testid="add-link-extra-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar link
                </Button>
              </div>
              <Input
                id="credito-link"
                placeholder="https://..."
                value={form.link}
                onChange={e => setForm({ ...form, link: e.target.value })}
              />
              {form.links_extras.map((lnk, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`link-extra-row-${idx}`}>
                  <Input
                    placeholder="https://..."
                    value={lnk}
                    onChange={e => updateLinkExtra(idx, e.target.value)}
                  />
                  <Button
                    type="button" variant="ghost" size="icon"
                    className="h-9 w-9 shrink-0 text-red-500 hover:text-red-600"
                    onClick={() => removeLinkExtra(idx)}
                    data-testid={`remove-link-extra-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="credito-login">Login</Label>
                <Input
                  id="credito-login"
                  placeholder="Usuario"
                  value={form.login}
                  onChange={e => setForm({ ...form, login: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="credito-senha">Senha</Label>
                <Input
                  id="credito-senha"
                  type="text"
                  placeholder="Senha"
                  value={form.senha}
                  onChange={e => setForm({ ...form, senha: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="credito-fornecedor">Fornecedor</Label>
              <Input
                id="credito-fornecedor"
                placeholder="Nome do fornecedor"
                value={form.fornecedor}
                onChange={e => setForm({ ...form, fornecedor: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="credito-contato1">Contato 1</Label>
                <Input
                  id="credito-contato1"
                  placeholder="(11) 99999-9999"
                  value={form.contato_1}
                  onChange={e => setForm({ ...form, contato_1: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="credito-contato2">Contato 2 (opcional)</Label>
                <Input
                  id="credito-contato2"
                  placeholder="(11) 99999-9999"
                  value={form.contato_2}
                  onChange={e => setForm({ ...form, contato_2: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="credito-descricao">Descricao</Label>
              <Textarea
                id="credito-descricao"
                placeholder="Anotacoes, observacoes, detalhes do plano..."
                value={form.descricao}
                onChange={e => setForm({ ...form, descricao: e.target.value })}
                rows={3}
                data-testid="credito-descricao-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="credito-save-btn">
              {saving ? "Salvando..." : (editing ? "Salvar" : "Adicionar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
